import { useState } from 'react';
import { execApi } from '../../../services/api';

// Additional payments panel. You stage items locally — add a few, remove a few —
// and only "שמור" persists them all in one batch (one refetch), so the page doesn't
// churn on every keystroke. Categories: non-clinical hours (priced hours×rate) plus
// flat pre-VAT lines (insurance/refund/sim/custom). קבוע repeats every month.

const ils = (n: number) => '₪' + (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// isQty = priced quantity×rate (hours or on-call days); the rest are a flat amount.
interface Cat {
  key: string; label: string; custom?: boolean;
  isQty?: boolean; qtyLabel?: string; qtyUnit?: string; rateLabel?: string; defaultRate?: number;
}
const CATS: Cat[] = [
  { key: 'hours', label: 'שעות לא קליניות', isQty: true, qtyLabel: 'שעות', qtyUnit: 'ש׳', rateLabel: 'תעריף/ש׳' },
  { key: 'oncall', label: 'כוננות', isQty: true, qtyLabel: 'ימים', qtyUnit: 'ימים', rateLabel: 'תעריף/יום', defaultRate: 1500 },
  { key: 'insurance', label: 'ביטוח' },
  { key: 'refund', label: 'החזר' },
  { key: 'sim', label: 'סים' },
  { key: 'custom', label: 'אחר', custom: true },
];
const catOf = (k: string) => CATS.find((c) => c.key === k) || CATS.find((c) => c.custom)!;
const isQtyCat = (k: string) => !!catOf(k).isQty;

interface Item {
  item_id: string; category: string; label: string | null;
  amount: number; hours: number | null; rate: number | null; recurring: boolean;
}
// A staged (not-yet-saved) new line.
interface Draft {
  category: string; label: string; amount: number;
  hours: number | null; rate: number | null; recurring: boolean;
}
interface Props {
  therapist: { name: string; additional_items: Item[] };
  month: string;
  nonClinicalRate: number;
  onSaved: () => void;
}

// Items the ENGINE produces rather than a person: they have no row in the store, so
// the ✕ would call delete with an id that does not exist and the line would simply
// come back on the next load — a delete button that does nothing is worse than none.
// They are still listed, and still counted in the total, because the money is real.
const COMPUTED = new Set(['cm_bonus', 'oncall']);

// One display row — an existing item or a staged draft — with a delete ✕.
function Row({ label, hours, rate, amount, recurring, isQty, qtyUnit, onDelete, computed }: {
  label: string; hours: number | null; rate: number | null; amount: number;
  recurring: boolean; isQty: boolean; qtyUnit: string; onDelete: () => void;
  computed?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-sm">
      <span className="text-gray-700">{label}</span>
      {isQty && hours != null && (
        <span className="text-gray-500 text-xs">{hours} {qtyUnit} × {ils(rate || 0)}</span>
      )}
      <b className="tabular-nums text-gray-800">{ils(amount)}</b>
      <span className={`text-xs rounded px-1.5 py-0.5 ${computed ? 'bg-blue-50 text-blue-700'
        : recurring ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
        {computed ? 'מחושב' : recurring ? 'קבוע' : 'חד-פעמי'}
      </span>
      {computed ? (
        <span className="mr-auto text-xs text-gray-500" title="מחושב מהנתונים — לא ניתן לעריכה או למחיקה מכאן">
          מחושב אוטומטית
        </span>
      ) : (
        <button onClick={onDelete} title="מחק"
          className="mr-auto text-gray-500 hover:text-red-600 text-base leading-none px-1">✕</button>
      )}
    </div>
  );
}

// The add form — builds a draft locally and hands it up; it never touches the server.
function AddRow({ nonClinicalRate, onAdd, onCancel }: {
  nonClinicalRate: number; onAdd: (d: Draft) => void; onCancel: () => void;
}) {
  const [cat, setCat] = useState('hours');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [hours, setHours] = useState('');
  const [rate, setRate] = useState(String(nonClinicalRate || ''));

  const c = catOf(cat);
  const preview = c.isQty
    ? Math.round((Number(hours) || 0) * (Number(rate) || 0) * 100) / 100
    : (Number(amount) || 0);
  const valid = preview > 0 && (!c.custom || label.trim() !== '');

  // Switching category resets the rate to that category's default (on-call = ₪1,500/day,
  // non-clinical hours = the therapist's hourly rate) — Basel can still override it.
  const changeCat = (k: string) => {
    setCat(k);
    const nc = catOf(k);
    if (nc.isQty) setRate(String(nc.defaultRate ?? nonClinicalRate ?? ''));
  };

  const submit = (recurring: boolean) => {
    onAdd({
      category: cat,
      label: c.custom ? label.trim() : c.label,
      amount: preview,
      hours: c.isQty ? Number(hours) || 0 : null,
      rate: c.isQty ? Number(rate) || 0 : null,
      recurring,
    });
  };

  // The recurring choice lives on the "add" itself: two buttons, no separate save round-trip.
  const [recurring, setRecurring] = useState(false);

  return (
    <div className="flex flex-wrap items-end gap-2 mt-2 p-2 bg-gray-50 rounded-lg">
      <label className="text-xs text-gray-500">
        סעיף
        <select value={cat} onChange={(e) => changeCat(e.target.value)}
          className="block mt-1 w-32 text-sm border border-gray-300 rounded px-2 py-1 bg-white">
          {CATS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </label>

      {c.custom && (
        <label className="text-xs text-gray-500">
          שם הסעיף
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="למשל: יום כיף"
            className="block mt-1 w-32 text-sm border border-gray-300 rounded px-2 py-1" />
        </label>
      )}

      {c.isQty ? (
        <>
          <label className="text-xs text-gray-500">
            {c.qtyLabel}
            <input type="number" step={c.key === 'oncall' ? '1' : '0.5'} value={hours} onChange={(e) => setHours(e.target.value)}
              className="block mt-1 w-20 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
          </label>
          <label className="text-xs text-gray-500">
            {c.rateLabel}
            <input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)}
              className="block mt-1 w-24 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
          </label>
        </>
      ) : (
        <label className="text-xs text-gray-500">
          מחיר (₪, לפני מע״מ)
          <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="block mt-1 w-28 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
        </label>
      )}

      <label className="text-xs text-gray-600 flex items-center gap-1 pb-1.5">
        <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
        קבוע (כל חודש)
      </label>

      {preview > 0 && <span className="pb-1.5 text-sm tabular-nums text-gray-500">= {ils(preview)}</span>}

      <div className="flex gap-1 pb-0.5">
        <button onClick={() => submit(recurring)} disabled={!valid}
          className="text-sm bg-gray-700 disabled:bg-gray-300 text-white rounded px-4 py-1">
          הוסף לרשימה
        </button>
        <button onClick={onCancel} className="text-sm text-gray-500 rounded px-2 py-1">ביטול</button>
      </div>
    </div>
  );
}

export function AdditionalItemsEditor({ therapist: t, month, nonClinicalRate, onSaved }: Props) {
  const [adding, setAdding] = useState(false);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const existing = (t.additional_items || []).filter((it) => !deletedIds.includes(it.item_id));
  const dirty = drafts.length > 0 || deletedIds.length > 0;
  const liveTotal =
    existing.reduce((s, i) => s + i.amount, 0) + drafts.reduce((s, d) => s + d.amount, 0);

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const d of drafts) {
        await execApi.saveSalaryItem({
          item_id: null, therapist: t.name, month, category: d.category, label: d.label,
          amount: isQtyCat(d.category) ? 0 : d.amount,
          hours: isQtyCat(d.category) ? d.hours : null,
          rate: isQtyCat(d.category) ? d.rate : null,
          recurring: d.recurring, active: true,
        });
      }
      for (const id of deletedIds) {
        await execApi.deleteSalaryItem({ item_id: id, month, therapist: t.name });
      }
      setDrafts([]); setDeletedIds([]); setAdding(false);
      onSaved(); // a single refetch after the whole batch
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium text-gray-700">תשלומים נוספים</span>
        <span className="text-xs text-gray-500">
          · סה״כ: <b className="tabular-nums text-gray-600">{ils(liveTotal)}</b>
          {dirty && <span className="text-amber-600"> (טרם נשמר)</span>}
        </span>
      </div>

      {(existing.length > 0 || drafts.length > 0) && (
        <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100">
          {existing.map((it) => (
            <Row key={it.item_id} label={it.label || catOf(it.category).label}
              hours={it.hours} rate={it.rate} amount={it.amount} recurring={it.recurring}
              isQty={isQtyCat(it.category)} qtyUnit={catOf(it.category).qtyUnit || ''}
              computed={COMPUTED.has(it.category)}
              onDelete={() => setDeletedIds([...deletedIds, it.item_id])} />
          ))}
          {drafts.map((d, i) => (
            <Row key={`d${i}`} label={d.label || catOf(d.category).label}
              hours={d.hours} rate={d.rate} amount={d.amount} recurring={d.recurring}
              isQty={isQtyCat(d.category)} qtyUnit={catOf(d.category).qtyUnit || ''}
              onDelete={() => setDrafts(drafts.filter((_, j) => j !== i))} />
          ))}
        </div>
      )}

      {adding ? (
        <AddRow nonClinicalRate={nonClinicalRate}
          onAdd={(d) => { setDrafts([...drafts, d]); setAdding(false); }}
          onCancel={() => setAdding(false)} />
      ) : (
        <div className="mt-2 flex items-center gap-3">
          <button onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900 font-medium rounded-lg border border-blue-200 hover:border-blue-300 px-3 py-1">
            <span className="text-base leading-none">+</span> הוסף סעיף
          </button>
          {dirty && (
            <button onClick={saveAll} disabled={saving}
              className="text-sm bg-blue-600 disabled:bg-gray-300 text-white rounded-lg px-5 py-1.5 font-medium">
              {saving ? 'שומר…' : 'שמור'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
